/**
 * platform-ai-lib.ts — Hàm thuần cho các trang Platform → AI (kiểm tra sơ bộ phía client;
 * thông báo lỗi của server khi lưu mới là nguồn chuẩn).
 */
import { vndToTokens, type CreditPack, type SalePrice } from '@/lib/ai-credits'
import type { CatalogModel, PlatformAiSettings } from '@/hooks/use-platform-ai'

export const TIER_OPTIONS = [
  { id: 'economy', label: 'Tiết kiệm' },
  { id: 'standard', label: 'Tiêu chuẩn' },
  { id: 'premium', label: 'Cao cấp' },
] as const

export const SOURCES_SUMMARY_LABEL: Record<string, string> = {
  platform: 'AI của eCDP',
  byok: 'Key của tôi',
  mixed: 'Hỗn hợp',
}

const isNum = (s: string) => s.trim() !== '' && Number.isFinite(Number(s))

/** Lỗi dòng danh mục model (giá âm / thiếu tên) — null nếu hợp lệ. */
export function catalogDraftError(d: { title: string; priceIn: string; priceOut: string; tier?: string }): string | null {
  if (!d.title.trim()) return 'Nhập tên hiển thị của model'
  if (!isNum(d.priceIn) || Number(d.priceIn) < 0) return 'Giá vào phải là số ≥ 0'
  if (!isNum(d.priceOut) || Number(d.priceOut) < 0) return 'Giá ra phải là số ≥ 0'
  return null
}

/** Điều chỉnh ví: số tiền (đ) khác 0 (âm = trừ) + lý do bắt buộc. */
export function adjustError(vndText: string, reason: string): string | null {
  if (!isNum(vndText) || Number(vndText) === 0) return 'Nhập số tiền khác 0 (số âm để trừ)'
  if (Math.abs(Number(vndText)) > 10_000_000_000) return 'Số tiền quá lớn'
  if (reason.trim().length < 5) return 'Nhập lý do điều chỉnh (ít nhất 5 ký tự)'
  return null
}

/** đ → đơn vị nội bộ của ví (3 số lẻ — API điều chỉnh nhận đơn vị). */
export function vndToUnits(vnd: number, creditValueVnd: number): number {
  if (!(creditValueVnd > 0)) return 0
  return Math.round((vnd / creditValueVnd) * 1000) / 1000
}

/** Kiểm tra cài đặt token trước khi lưu. */
export function settingsError(s: PlatformAiSettings): string | null {
  if (!(s.creditValueVnd > 0)) return 'Đơn vị tính nội bộ phải lớn hơn 0'
  if (!(s.usdVnd > 0)) return 'Tỷ giá USD phải lớn hơn 0'
  if (!(s.markupPercent >= 0) || s.markupPercent > 1000) return 'Biên giá bán ra phải từ 0 đến 1000%'
  if (!(s.embeddingOverageCreditsPer1000 >= 0)) return 'Mức trừ / 1.000 lần nhúng vượt trần phải ≥ 0'
  const ids = new Set<string>()
  for (const p of s.packs) {
    if (!p.id.trim()) return 'Mỗi gói mua thêm cần mã'
    if (ids.has(p.id)) return `Mã gói “${p.id}” bị trùng`
    ids.add(p.id)
    if (!(p.credits > 0) || !Number.isInteger(p.credits)) return `Gói “${p.id}”: giá trị nạp phải lớn hơn 0`
    if (!(p.price >= 0)) return `Gói “${p.id}”: giá phải ≥ 0`
  }
  return null
}

/** Mã gói mới không trùng: pack-1, pack-2, … */
export function newPackId(packs: Pick<CreditPack, 'id'>[]): string {
  let n = packs.length + 1
  while (packs.some((p) => p.id === `pack-${n}`)) n++
  return `pack-${n}`
}

/**
 * Đơn vị / lượt ước tính (cũ — giao diện thôi dùng, giữ cho test cũ): (3.000 × giá vào + 400 × giá ra) / 1e6 × tỷ giá ×
 * (1 + biên) ÷ đơn vị tính nội bộ.
 */
export function previewCreditsPerTurn(priceIn: number, priceOut: number, s: Pick<PlatformAiSettings, 'usdVnd' | 'markupPercent' | 'creditValueVnd'>): number {
  if (!(s.creditValueVnd > 0)) return 0
  const usd = (3000 * priceIn + 400 * priceOut) / 1e6
  return Math.round((usd * s.usdVnd * (1 + s.markupPercent / 100) / s.creditValueVnd) * 1000) / 1000
}

// ── Giá bán ra + quy đổi ≈ token (xem trước khi sửa biên / tỷ giá) ─────────────

/** Lượt chuẩn để tính giá bình quân (khớp server: 3.000 vào : 400 ra). */
export const TURN = { tokensIn: 3_000, tokensOut: 400 }

/** Giá bán ra / 1 triệu token (đ) = giá mua vào USD × tỷ giá × (1 + biên %) — cùng công thức server (token-math.ts). */
export function salePriceFor(priceIn: number, priceOut: number, s: Pick<PlatformAiSettings, 'usdVnd' | 'markupPercent'>): SalePrice {
  const k = (Number(s.usdVnd) || 0) * (1 + (Number(s.markupPercent) || 0) / 100)
  const r2 = (n: number) => Math.round(n * 100) / 100
  const inVnd = Math.max(0, priceIn) * k
  const outVnd = Math.max(0, priceOut) * k
  return { inVnd: r2(inVnd), outVnd: r2(outVnd), blendedVnd: r2((TURN.tokensIn * inVnd + TURN.tokensOut * outVnd) / (TURN.tokensIn + TURN.tokensOut)) }
}

const sellable = (m: CatalogModel) => m.kind === 'chat' && m.active && m.managedEnabled && m.priceIn + m.priceOut > 0
const modelCost = (m: CatalogModel) => TURN.tokensIn * m.priceIn + TURN.tokensOut * m.priceOut

/** Model chat đang bán (để chọn làm model tham chiếu), rẻ trước. */
export function referenceCandidates(catalog: CatalogModel[] | null | undefined): CatalogModel[] {
  return (catalog ?? []).filter(sellable).sort((a, b) => modelCost(a) - modelCost(b))
}

/** Model tham chiếu: model người dùng chọn → model server đánh dấu (mặc định nền tảng) → model đang bán rẻ nhất. */
export function referenceModelOf(catalog: CatalogModel[] | null | undefined, pickedKey?: string | null): CatalogModel | null {
  const list = referenceCandidates(catalog)
  if (pickedKey) {
    const hit = list.find((m) => `${m.provider}:${m.model}` === pickedKey)
    if (hit) return hit
  }
  return list.find((m) => m.isReference) ?? list[0] ?? null
}

/** Giá trị nạp (đ) của gói = đơn vị × đơn vị tính nội bộ. */
export function packValueOf(p: Pick<CreditPack, 'credits'>, s: Pick<PlatformAiSettings, 'creditValueVnd'>): number {
  return Math.round(p.credits * s.creditValueVnd)
}

/** Giá trị nạp (đ) nhập vào → đơn vị (số nguyên, server yêu cầu). */
export function unitsForValue(vnd: number, s: Pick<PlatformAiSettings, 'creditValueVnd'>): number {
  return s.creditValueVnd > 0 ? Math.max(0, Math.round(vnd / s.creditValueVnd)) : 0
}

/** ≈ token của MỘT lượng đơn vị theo model tham chiếu + cài đặt đang sửa. null = không có model tham chiếu. */
export function unitsToTokensRef(units: number, s: Pick<PlatformAiSettings, 'creditValueVnd' | 'usdVnd' | 'markupPercent'>, ref: Pick<CatalogModel, 'priceIn' | 'priceOut'> | null): number | null {
  if (!ref) return null
  return vndToTokens(units * s.creditValueVnd, salePriceFor(ref.priceIn, ref.priceOut, s).blendedVnd)
}
